import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { mintPortalToken, registerPortalToken, popupGuardScript } from '../utils/popupGuard';

const WINDOW_FEATURES = 'width=1440,height=810,left=200,top=200';

export default function WindowPortal({ children, title, onClose }) {
  const containerEl = useRef(document.createElement('div'));

  useLayoutEffect(() => {
    const win = window.open('', '', WINDOW_FEATURES);
    if (!win) {
      if (typeof onClose === 'function') onClose();
      return;
    }

    const doc = win.document;

    const headNodes = document.querySelectorAll('link[rel="stylesheet"], style');
    headNodes.forEach((node) => {
      try { doc.head.appendChild(node.cloneNode(true)); } catch (_) {}
    });

    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) doc.head.appendChild(viewport.cloneNode(true));

    doc.title = title || '';
    Object.assign(doc.body.style, {
      margin: '0',
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      background: 'transparent',
    });

    doc.body.appendChild(containerEl.current);

    const handleUnload = () => { if (typeof onClose === 'function') onClose(); };
    win.addEventListener('beforeunload', handleUnload);

    // beforeunload doesn't always fire when the popup is closed natively;
    // if the session stays mounted it keeps its websocket reconnect loop
    // running invisibly and hammers the device. Poll as a fallback.
    const closePoll = setInterval(() => {
      if (win.closed) handleUnload();
    }, 1000);

    // This React tree (and the VNC session driving the popup) runs in the
    // DASHBOARD tab. If that tab reloads or navigates away, the popup
    // survives as a frozen ghost — spinner forever, no retry — which reads
    // as "VNC is stuck". Close it from both sides:
    // 1) instant: opener pagehide (fires reliably on refresh/navigation);
    const onOpenerGone = () => { try { win.close(); } catch (_) {} };
    window.addEventListener('pagehide', onOpenerGone);
    // 2) fallback: the popup polls whether the document that opened it is
    //    still alive by reading a per-mount token off `window.opener`
    //    (covers opener death where pagehide never fired). This must NOT
    //    depend on timers running in the dashboard tab: Chrome throttles a
    //    hidden tab's timers to one wake-up per minute after ~60s in the
    //    background, and the previous 1s-heartbeat/15s-watchdog design
    //    closed healthy sessions ~76s after users left the dashboard tab.
    //    See utils/popupGuard.js.
    const token = mintPortalToken();
    const unregisterToken = registerPortalToken(window, token);
    try {
      const script = doc.createElement('script');
      script.textContent = popupGuardScript(token);
      doc.body.appendChild(script);
    } catch (_) {}

    return () => {
      clearInterval(closePoll);
      unregisterToken();
      window.removeEventListener('pagehide', onOpenerGone);
      win.removeEventListener('beforeunload', handleUnload);
      win.close();
    };
  }, []);

  return createPortal(children, containerEl.current);
}
